// ReplicatorGob — the MP cloning structure: a unit parked at the input (top) gets cloned
// to output2 for a third of its cost while the original warps to output1; output bays are
// kept clear by polite move requests. + ActivatorGob (the pressure plate that enables
// replicators via triggers — an AnimGob polling for a munt on top every 3 updates).
// game/Replicator.cpp (all 564 lines) + ht.h:8091-8096 + GetReplicationCost (ht.h:6745).

import type { SimContext } from '../context.ts';
import { kfGobMobileUnit, kfGobSelected } from './gob.ts';
import { StructGob } from './struct.ts';
import { MobileUnitGob, kfMuntAtReplicatorInput } from './mobileunit.ts';
import { MinerGob } from './miner.ts';
import { AnimGob, kfAnmLoop, kfAnmSurfaceDecalLayer } from './animgob.ts';
import {
  StructConsts,
  MobileUnitConsts,
  InitStructConstsClass,
  kwcTileHalf,
} from './unitconsts.ts';
import { kgtReplicator, kgtActivator, kgtGalaxMiner } from './gobtypes.ts';
import { kutReplicator } from '../player/side.ts';
import type { Player } from '../player/player.ts';
import {
  Message,
  newMessage,
  knHandled,
  kstReservedGlobal,
  kmidReservedUpdate,
  kmidPlaySfx,
  kmidMoveCommand,
  kgidNull,
  ksmidNull,
  kstIdle,
} from '../sm/message.ts';
import { kfAniLoop } from '../anim/animation.ts';
import { TcFromWc, WcFromTc } from '../math/coords.ts';
import { idiv } from '../math/int32.ts';
import { kbfStructure, kbfMobileUnit } from '../terrain/terrainmap.ts';
import { IsTileFree } from './tilesearch.ts';
import { CreateGob, registerGobType } from './factory.ts';
import { knLimitMobileUnit } from './gobmgr.ts';
import { IniScanf } from '../data/ini.ts';
import type { IniReader } from '../data/ini.ts';
import {
  ksfxReplicatorBuild,
  ksfxReplicatorOn,
  ksfxReplicatorOff,
  ksfxActivatorOn,
  ksfxActivatorOff,
  SfxFromCategory,
} from '../data/soundeffects.ts';
import type { TPoint } from '../types.ts';

// ht.h:8091-8096 — the dock offsets (input top-center, outputs bottom left/right)
export const kdtxReplicatorInput = 2;
export const kdtyReplicatorInput = 0;
export const kdtxReplicatorOutput1 = 1;
export const kdtyReplicatorOutput1 = 2;
export const kdtxReplicatorOutput2 = 3;
export const kdtyReplicatorOutput2 = 2;

// ht.h:6745
export function GetReplicationCost(nCost: number): number {
  return idiv(nCost, 3);
}

// game/Replicator.cpp:442-450 — the static input-point registry is ctx-owned
export function AddReplicatorInputPoint(ctx: SimContext, tx: number, ty: number): void {
  if (ctx.cReplicatorInputPoints >= 9) return; // ARRAYSIZE(10) - 1
  const slot = ctx.replicatorInputPoints[ctx.cReplicatorInputPoints];
  slot.tx = tx;
  slot.ty = ty;
  ctx.cReplicatorInputPoints++;
}

// game/Replicator.cpp:452-462 — BUG-COMPAT (sim-quirks A4): the C++ tests `atpt[n].ty` (truthiness, not
// equality!), so a ty==0 point never matches and the first tx-match with ANY nonzero ty
// is the one removed. Preserved verbatim.
// The memmove shifts entries down but NEVER CLEARS the vacated tail slot — the stale
// duplicate stays in the raw array and is SERIALIZED (Replicator.cpp:106 writes
// sizeof(s_atptInput)). The C++ also scans ALL 10 slots, so a tx-match on a STALE
// slot would memmove a negative count — heap corruption; we throw instead of
// emulating that (unreachable from live state, since live points precede stale ones).
export function RemoveReplicatorInputPoint(ctx: SimContext, tx: number, _ty: number): void {
  const a = ctx.replicatorInputPoints;
  for (let n = 0; n < a.length; n++) {
    if (a[n].tx === tx && a[n].ty) {
      if (n >= ctx.cReplicatorInputPoints) {
        throw new Error(
          'RemoveReplicatorInputPoint matched a stale slot — the C++ corrupts the heap here',
        );
      }
      for (let i = n; i < ctx.cReplicatorInputPoints - 1; i++) {
        a[i].tx = a[i + 1].tx;
        a[i].ty = a[i + 1].ty;
      }
      ctx.cReplicatorInputPoints--;
      return;
    }
  }
}

// game/Replicator.cpp:20-49 (the activator anim preloads via the registry)
export function InitReplicatorClass(ctx: SimContext, pini: IniReader): boolean {
  const c = new StructConsts().bind(ctx);
  c.gt = kgtReplicator;
  c.ut = kutReplicator;
  // Sound effects (Replicator.cpp:26; the :29-37 block is #if 0)
  c.sfxUnitReady = ksfxReplicatorBuild;
  return InitStructConstsClass(ctx, c, pini);
}

export class ReplicatorGob extends StructGob {
  m_fEnabled = false;
  m_fReplicating = false;
  m_ifrmLights = 0;
  m_pplrNeedCredits: Player | null = null;

  // game/Replicator.cpp:59-64
  constructor(ctx: SimContext) {
    super(ctx, ctx.unitConsts[kutReplicator] as StructConsts);
  }

  // game/Replicator.cpp:66-79 — clear the entrance and exits in the structure footprint
  override Init(wx: number, wy: number, pplr: Player, fxHealth: number, ff: number): boolean {
    if (!super.Init(wx, wy, pplr, fxHealth, ff)) return false;

    const ptrmap = this.ctx.trmap!;
    ptrmap.ClearFlags(
      TcFromWc(this.m_wx) + kdtxReplicatorInput,
      TcFromWc(this.m_wy) + kdtyReplicatorInput,
      1,
      1,
      kbfStructure,
    );
    ptrmap.ClearFlags(
      TcFromWc(this.m_wx) + kdtxReplicatorOutput1 - 1,
      TcFromWc(this.m_wy) + kdtyReplicatorOutput1 + 1,
      1,
      2,
      kbfStructure,
    );
    ptrmap.ClearFlags(
      TcFromWc(this.m_wx) + kdtxReplicatorOutput2 + 1,
      TcFromWc(this.m_wy) + kdtyReplicatorOutput2 + 1,
      1,
      2,
      kbfStructure,
    );

    return true;
  }

  // game/Replicator.cpp:180-185
  GetInputTilePosition(): TPoint {
    const tpt: TPoint = { tx: 0, ty: 0 };
    this.GetTilePosition(tpt);
    tpt.tx += kdtxReplicatorInput;
    tpt.ty += kdtyReplicatorInput;
    return tpt;
  }

  // game/Replicator.cpp:187-194 — triggers call this (the Activator wiring)
  // game/Replicator.cpp:83-112 (knVerReplicatorGobState 3) — the CLASS-static input
  // points + count round-trip through every instance (last one wins, as in C++)
  override LoadState(pstm: import('../sm/stream.ts').Stream): boolean {
    const nVer = pstm.ReadByte();
    if (nVer !== 3) return false;
    this.m_fEnabled = pstm.ReadByte() !== 0;
    this.m_fReplicating = pstm.ReadByte() !== 0;
    this.m_ifrmLights = pstm.ReadByte();
    // the C++ reads the RAW 40-byte array back — stale tail slots included
    this.ctx.cReplicatorInputPoints = pstm.ReadWord();
    for (let i = 0; i < 10; i++) {
      const slot = this.ctx.replicatorInputPoints[i];
      slot.tx = (pstm.ReadWord() << 16) >> 16;
      slot.ty = (pstm.ReadWord() << 16) >> 16;
    }
    const pid = pstm.ReadWord();
    if (pid !== 0xffff) this.m_pplrNeedCredits = this.ctx.plrm.GetPlayerFromPid(pid);
    return super.LoadState(pstm);
  }

  // Replicator.cpp:146-164 — the C++ Draw() SETS m_ani's strip/frame as a render
  // trick (strip 0/2 then 1/3, lights strip), so the SAVED values are whatever the
  // last client-viewport draw left behind — unreproducible headless (sim-quirks B21).
  protected override aniSaveIsDrawMutated(): boolean {
    return true;
  }

  override SaveState(pstm: import('../sm/stream.ts').Stream): boolean {
    pstm.WriteByte(3);
    pstm.WriteByte(this.m_fEnabled ? 1 : 0);
    pstm.WriteByte(this.m_fReplicating ? 1 : 0);
    pstm.WriteByte(this.m_ifrmLights);
    pstm.WriteWord(this.ctx.cReplicatorInputPoints); // s_cReplicators
    for (let i = 0; i < 10; i++) {
      // the raw TPoint[10] image — stale tail included
      pstm.WriteWord(this.ctx.replicatorInputPoints[i].tx);
      pstm.WriteWord(this.ctx.replicatorInputPoints[i].ty);
    }
    const pid = this.m_pplrNeedCredits === null ? 0xffff : this.m_pplrNeedCredits.GetId();
    pstm.WriteWord(pid);
    return super.SaveState(pstm);
  }

  Enable(fEnable: boolean): void {
    if (this.m_fEnabled !== fEnable) {
      this.ctx.host.PlaySfx(fEnable ? ksfxReplicatorOn : ksfxReplicatorOff); // Replicator.cpp:190
      this.m_fEnabled = fEnable;
      this.MarkRedraw();
    }
  }

  // game/Replicator.cpp:196-202
  override Activate(): void {
    const tpt: TPoint = { tx: 0, ty: 0 };
    this.GetTilePosition(tpt);
    AddReplicatorInputPoint(this.ctx, tpt.tx + kdtxReplicatorInput, tpt.ty + kdtyReplicatorInput);
    super.Activate();
  }

  // game/Replicator.cpp:204-210
  override Deactivate(): void {
    const tpt: TPoint = { tx: 0, ty: 0 };
    this.GetTilePosition(tpt);
    RemoveReplicatorInputPoint(
      this.ctx,
      tpt.tx + kdtxReplicatorInput,
      tpt.ty + kdtyReplicatorInput,
    );
    super.Deactivate();
  }

  // game/Replicator.cpp:212-251 — true = jammed
  ClearOutputBay(txBay: number, tyBay: number, txOut: number, tyOut: number): boolean {
    for (
      let gid = this.ctx.gobm.GetFirstGid(txBay, tyBay);
      gid !== kgidNull;
      gid = this.ctx.gobm.GetNextGid(gid)
    ) {
      const pmunt = this.ctx.gobm.GetGob(gid) as MobileUnitGob | null;
      if (pmunt === null) continue;
      if (!(pmunt.GetFlags() & kfGobMobileUnit)) continue;

      // Ask it to move on (unless it's already trying)
      if (pmunt.IsReadyForCommand() && !pmunt.IsMobile()) {
        const msgT = newMessage(kmidMoveCommand, this.m_gid, pmunt.GetId());
        msgT.MoveCommand.wptTarget.wx = WcFromTc(txOut) + kwcTileHalf;
        msgT.MoveCommand.wptTarget.wy = WcFromTc(tyOut) + kwcTileHalf;
        msgT.MoveCommand.gidTarget = kgidNull;
        msgT.MoveCommand.wptTargetCenter.wx = msgT.MoveCommand.wptTarget.wx;
        msgT.MoveCommand.wptTargetCenter.wy = msgT.MoveCommand.wptTarget.wy;
        msgT.MoveCommand.tcTargetRadius = 0;
        msgT.MoveCommand.wcMoveDistPerUpdate = (
          pmunt.GetConsts() as MobileUnitConsts
        ).GetMoveDistPerUpdate();
        this.ctx.smm!.SendMsg(msgT);
      }

      return true; // jammed
    }

    // A unit moving INTO the bay also jams it
    if (!IsTileFree(this.ctx, txBay, tyBay, kbfMobileUnit).free) return true;

    return false;
  }

  // game/Replicator.cpp:253-414
  override ProcessStateMachineMessage(st: number, pmsg: Message): number {
    if (st === kstReservedGlobal && pmsg.mid === kmidPlaySfx) {
      this.ctx.host.PlaySfx(pmsg.PlaySfx.sfx);
      return knHandled;
    }

    if (st === kstIdle && pmsg.mid === kmidReservedUpdate) {
      const tpt: TPoint = { tx: 0, ty: 0 };
      this.GetTilePosition(tpt);

      // Clear left and right output bays
      let pplrNeedCredits: Player | null = null;
      let fJammed = this.ClearOutputBay(
        tpt.tx + kdtxReplicatorOutput1,
        tpt.ty + kdtyReplicatorOutput1,
        tpt.tx + kdtxReplicatorOutput1 - 1,
        tpt.ty + kdtyReplicatorOutput1 + 1,
      );
      fJammed =
        this.ClearOutputBay(
          tpt.tx + kdtxReplicatorOutput2,
          tpt.ty + kdtyReplicatorOutput2,
          tpt.tx + kdtxReplicatorOutput2 + 1,
          tpt.ty + kdtyReplicatorOutput2 + 1,
        ) || fJammed;

      // Outputs clear: consider the input
      if (this.m_fEnabled && !fJammed) {
        for (
          let gid = this.ctx.gobm.GetFirstGid(
            tpt.tx + kdtxReplicatorInput,
            tpt.ty + kdtyReplicatorInput,
          );
          gid !== kgidNull;
          gid = this.ctx.gobm.GetNextGid(gid)
        ) {
          const pmunt = this.ctx.gobm.GetGob(gid) as MobileUnitGob | null;
          if (pmunt === null) continue;
          if (!(pmunt.GetFlags() & kfGobMobileUnit)) continue;

          // Only a unit that has parked itself at the input is ready
          if (!(pmunt.GetMobileUnitFlags() & kfMuntAtReplicatorInput)) continue;

          // At the unit limit? (the local-player alert throttle is host)
          if (!this.ctx.gobm.IsBelowLimit(knLimitMobileUnit, pmunt.GetOwner() as Player)) {
            continue;
          }

          // Enough credits for the replication?
          const pmuntc = pmunt.GetConsts() as MobileUnitConsts;
          const pplr = pmunt.GetOwner() as Player;
          const cCredits = pplr.GetCredits();

          const nCost = pmuntc.GetCost();
          const cReplicationCost = GetReplicationCost(nCost);
          if (cCredits < cReplicationCost) {
            pplrNeedCredits = pplr;
            break;
          }

          // Take the money!
          pplr.SetCredits(cCredits - cReplicationCost, true);

          // (Hilight(false): presentation)

          // Start the replicating animation
          this.m_fReplicating = true;
          this.m_ifrmLights = -1;

          const pmuntClone = CreateGob(this.ctx, pmunt.GetType()) as MobileUnitGob | null;
          if (pmuntClone !== null) {
            pmuntClone.Init(
              WcFromTc(tpt.tx + kdtxReplicatorOutput2),
              WcFromTc(tpt.ty + kdtyReplicatorOutput2),
              pmunt.GetOwner() as Player,
              pmunt.GetHealth(),
              0,
            );

            // The clone acquires the original's selection state
            if (pmunt.GetFlags() & kfGobSelected) pmuntClone.Select(true);

            // Replicated miners lose their galaxite load
            if (pmunt.GetType() === kgtGalaxMiner) (pmunt as MinerGob).SetGalaxiteAmount(0);

            // Group membership clones too (UnitGroupMgr lands at M3's trigger pass)
            this.ctx.unitGroupOfGob?.(pmunt.GetId())?.AddUnit?.(pmuntClone, true);
          }

          // Warp the original to the left output port
          const ptrmap = this.ctx.trmap!;
          ptrmap.ClearFlags(
            tpt.tx + kdtxReplicatorInput,
            tpt.ty + kdtyReplicatorInput,
            1,
            1,
            kbfMobileUnit,
          );
          ptrmap.SetFlags(
            tpt.tx + kdtxReplicatorOutput1,
            tpt.ty + kdtyReplicatorOutput1,
            1,
            1,
            kbfMobileUnit,
          );
          pmunt.SetPosition(
            WcFromTc(tpt.tx + kdtxReplicatorOutput1) + kwcTileHalf,
            WcFromTc(tpt.ty + kdtyReplicatorOutput1) + kwcTileHalf,
          );

          pmunt.SetMobileUnitFlags(pmunt.GetMobileUnitFlags() & ~kfMuntAtReplicatorInput);

          this.ctx.host.PlaySfx(ksfxReplicatorBuild); // Replicator.cpp:366

          // Announce the new unit twice, a quarter second apart
          // (Replicator.cpp:370-386). The DELAYED MESSAGES are sim state and
          // the pick is SERIALIZED into the message image — drawn HERE from
          // the ASYNC rng (glAsyncSeed), exactly the C++ point; only the host
          // ever reads the payload (sim-quirks B19: per-machine by design).
          const sfx = SfxFromCategory(pmuntc.sfxcSelect, this.ctx.rngAsync);
          let msgT = newMessage(kmidPlaySfx, this.m_gid, this.m_gid);
          msgT.PlaySfx.sfx = sfx;
          this.ctx.smm!.SendDelayedMsg(msgT, 24);

          msgT = newMessage(kmidPlaySfx, this.m_gid, this.m_gid);
          msgT.PlaySfx.sfx = sfx;
          this.ctx.smm!.SendDelayedMsg(msgT, 48);
          break;
        }
      }

      if (this.m_fReplicating) {
        this.m_ifrmLights++;
        const panid = this.ctx.animDataFor(this.m_pstruc.szAniName);
        const nStrip = panid.GetStripIndex('l 0');
        if (idiv(this.m_ifrmLights, 2) >= panid.GetFrameCount(nStrip)) {
          this.m_ifrmLights = -1;
          this.m_fReplicating = false;
        }
        this.MarkRedraw();
      }
      this.WaitingForCredits(pplrNeedCredits !== null, false, pplrNeedCredits);
      this.m_unvl.MinSkip();
      this.DefUpdate();
      return knHandled;
    }

    return super.ProcessStateMachineMessage(st, pmsg);
  }

  // game/Replicator.cpp:416-434 — the credits-needed count belongs to the UNIT's player
  override WaitingForCredits(
    fNeed: boolean,
    fOverride: boolean = false,
    pplr: Player | null = null,
  ): void {
    if (fNeed) {
      if (fOverride) pplr = this.m_pplrNeedCredits;
      else this.m_pplrNeedCredits = pplr;
    } else {
      pplr = this.m_pplrNeedCredits;
      this.m_pplrNeedCredits = null;
    }

    super.WaitingForCredits(fNeed, fOverride, pplr);
  }
}

// game/Replicator.cpp:475-562 — the pressure plate
export class ActivatorGob extends AnimGob {
  m_fActivated = false;

  // game/Replicator.cpp:493-509 (knVerActivatorGobState 1)
  override LoadState(pstm: import('../sm/stream.ts').Stream): boolean {
    const nVer = pstm.ReadByte();
    if (nVer !== 1) return false;
    this.m_fActivated = pstm.ReadByte() !== 0;
    this.m_ani.Init(this.ctx.animDataFor('activator.anir'));
    this.m_ani.LoadState(pstm);
    this.m_wfAnm = kfAnmLoop | kfAnmSurfaceDecalLayer;
    return super.LoadState(pstm);
  }

  override SaveState(pstm: import('../sm/stream.ts').Stream): boolean {
    pstm.WriteByte(1);
    pstm.WriteByte(this.m_fActivated ? 1 : 0);
    this.m_ani.SaveState(pstm);
    return super.SaveState(pstm);
  }

  override GetType(): number {
    return kgtActivator;
  }

  // game/Replicator.cpp:475-489 — "%*d ,%d ,%d ,%d" (side unused beyond parse)
  InitFromArgs(value: string): boolean {
    const { count, values } = IniScanf(value, '%*d ,%d ,%d ,%d');
    if (count < 3) return false;
    const [, tx, ty] = values as number[];

    // Keep players from building on top of Activators
    this.ctx.trmap!.SetFlags(tx, ty, 1, 1, 0x20 /* kbfReserved */);

    return this.Init(
      WcFromTc(tx),
      WcFromTc(ty),
      kfAnmLoop | kfAnmSurfaceDecalLayer,
      this.ctx.animDataFor('activator.anir'),
      0,
      ksmidNull,
    );
  }

  override IsSavable(): boolean {
    return true;
  }

  // game/Replicator.cpp:524-561 — poll every 3 updates for a munt on top.
  // BUG-COMPAT (sim-quirks A5): the C++ passes kfAnmSurfaceDecalLayer (0x0008) as an ANI flag, which
  // collides with kfAniResetWhenDone — preserved by passing the same value.
  override ProcessStateMachineMessage(st: number, pmsg: Message): number {
    if (st === kstReservedGlobal && pmsg.mid === kmidReservedUpdate) {
      let fActivated = false;
      for (
        let gid = this.ctx.gobm.GetFirstGid(TcFromWc(this.m_wx), TcFromWc(this.m_wy));
        gid !== kgidNull;
        gid = this.ctx.gobm.GetNextGid(gid)
      ) {
        const pmunt = this.ctx.gobm.GetGob(gid);
        if (pmunt === null) continue;
        if (!(pmunt.GetFlags() & kfGobMobileUnit)) continue;
        fActivated = true;
        break;
      }

      if (fActivated !== this.m_fActivated) {
        this.m_fActivated = fActivated;
        this.StartAnimation(this.m_ani, fActivated ? 1 : 0, 0, kfAniLoop | kfAnmSurfaceDecalLayer);
        this.ctx.host.PlaySfx(fActivated ? ksfxActivatorOn : ksfxActivatorOff); // Replicator.cpp:542
      }

      this.m_unvl.MinSkip(3);

      return super.ProcessStateMachineMessage(st, pmsg);
    }
    return super.ProcessStateMachineMessage(st, pmsg);
  }
}

// self-register (see factory.ts)
registerGobType(kgtReplicator, (ctx) => new ReplicatorGob(ctx));
registerGobType(kgtActivator, (ctx) => new ActivatorGob(ctx));
